import {
    type MutableRefObject,
    type ReactNode,
    useCallback,
    useMemo,
    useEffect,
    createContext,
    useContext,
} from 'react';
import { FormProvider, useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
    type PointInput,
    type Project,
    type UpdateProjectInput,
    type ProjectAttribute,
} from '@gql/graphql';
import {
    CREATE_PROJECT,
    UPDATE_PROJECT,
    BULK_UPDATE_PROJECT_ATTRIBUTE,
} from '@api/mutations/projects';
import { useToast } from '@contexts/GlobalUiContext';
import {
    GET_ALL_PROJECTS,
    GET_PROJECT_SUMMARY,
    GET_PROJECT_CRA_COUNT,
} from '@api/queries/projects';
import { Route } from '@constants/routes';
import { sortBySequence } from '@utils/sorting';
import { hasDirtyFields } from '@utils/hasDirtyFields';

export interface ProjectFormReturn {
    form: ReturnType<typeof useForm<ProjectFormAttributes>>;
    onSubmit: (closeDialog?: () => void) => Promise<boolean>;
}

export type TreeSelectSelectionKeysType = Record<
    string,
    {
        checked?: boolean;
        partialChecked?: boolean;
    }
>;

export interface ProjectFormAttributes {
    // Details
    id?: string;
    name: string;
    description: string;
    typeID: string;
    statusID: string;
    milestoneID: string;
    organizationID: string;
    userIDs: string[];
    securityResponsibleID: string;
    // Requirement
    deliveryCountryKeys: TreeSelectSelectionKeysType;
    // Standards
    standardProfileIDs: string[];
    // Customer
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerAddress: string;
    plantName: string;
    plantExternalID: string;
    locationStreet: string;
    locationPostalCode: string;
    locationCity: string;
    locationCountry: string;
    pointInput?: PointInput;
    longitude?: number;
    latitude?: number;
    projectAttributes?: ProjectAttribute[];
}

const ProjectFormContext = createContext<ProjectFormReturn | null>(null);

export function useProjectFormContext(): ProjectFormReturn {
    const context = useContext(ProjectFormContext);
    if (!context) {
        throw new Error(
            'useProjectFormContext must be used within ProjectFormStepperWrapper',
        );
    }
    return context;
}

interface ProjectFormStepperWrapperProps {
    children: ReactNode;
    project?: Project;
    isDirtyRef?: MutableRefObject<() => boolean>;
}

export function ProjectFormStepperWrapper({
    children,
    project,
    isDirtyRef,
}: ProjectFormStepperWrapperProps): ReactNode {
    const formReturn = useProjectForm(project);

    if (isDirtyRef) {
        isDirtyRef.current = () =>
            hasDirtyFields(formReturn.form.formState.dirtyFields);
    }

    return (
        <ProjectFormContext.Provider value={formReturn}>
            <FormProvider {...formReturn.form}>{children}</FormProvider>
        </ProjectFormContext.Provider>
    );
}

export function useProjectForm(project?: Project): ProjectFormReturn {
    const [bulkUpdate] = useMutation(BULK_UPDATE_PROJECT_ATTRIBUTE);
    const navigate = useNavigate();

    const projectAttributes = useMemo(
        () => sortBySequence(project?.projectAttributes ?? [], true),
        [project],
    );

    const form = useForm<ProjectFormAttributes>({
        defaultValues: {
            id: project?.id,
            name: project?.name,
            description: project?.description,
            typeID: project?.type?.id,
            statusID: project?.status?.id,
            milestoneID: project?.milestone?.id,
            organizationID: project?.organization?.id,
            userIDs:
                project?.participants
                    ?.filter(
                        (participant) =>
                            participant?.__typename === 'ProjectUser',
                    )
                    .map((participant) => participant.user.id) ?? [],
            securityResponsibleID: project?.securityResponsible?.id,
            deliveryCountryKeys: Object.fromEntries(
                (project?.projectCountries ?? []).map(({ country }) => [
                    country.id,
                    { checked: true },
                ]),
            ),
            // TODO: standardProfileIDs are not persisted on edit — UpdateProjectInput does not support this field yet (backend support needed)
            standardProfileIDs:
                project?.projectStandardProfiles
                    ?.map((profile) => profile.standardProfile?.id)
                    .filter(Boolean) ?? [],
            customerName: project?.customerName ?? '',
            customerEmail: project?.customerEmail ?? '',
            customerPhone: project?.customerPhone ?? '',
            customerAddress: project?.customerAddress ?? '',
            plantName: project?.plantName ?? '',
            plantExternalID: project?.plantExternalID ?? '',
            locationStreet: project?.locationStreet ?? '',
            locationPostalCode: project?.locationPostalCode ?? '',
            locationCity: project?.locationCity ?? '',
            locationCountry: project?.locationCountry ?? '',
            pointInput: project?.point ?? undefined,
            latitude: project?.point?.latitude,
            longitude: project?.point?.longitude,
            projectAttributes,
        },
        mode: 'onChange',
    });
    const { getValues, reset } = form;

    const { replace } = useFieldArray({
        control: form.control,
        name: 'projectAttributes',
        keyName: 'id',
    });

    useEffect(() => {
        replace(projectAttributes);
        reset(getValues(), { keepValues: true });
    }, [getValues, projectAttributes, replace, reset]);

    const [updateProject] = useMutation(UPDATE_PROJECT, {
        refetchQueries: [GET_PROJECT_SUMMARY, GET_PROJECT_CRA_COUNT],
    });
    const { pushSuccessToast, pushErrorToast } = useToast();

    const [createProject] = useMutation(CREATE_PROJECT, {
        refetchQueries: [GET_ALL_PROJECTS],
    });

    const getCheckedCountryIDs = useCallback((): string[] => {
        const { deliveryCountryKeys } = getValues();
        return Object.entries(deliveryCountryKeys)
            .filter(([, v]) => v.checked)
            .map(([id]) => id);
    }, [getValues]);

    const handleProjectUpdate = useCallback(
        async (closeDialog?: () => void): Promise<boolean> => {
            const countryIDs = getCheckedCountryIDs();
            const payload: UpdateProjectInput = {
                id: project?.id ?? '',
                ...getValues(),
                countryIDs,
            };

            try {
                const customAttributes = getValues('projectAttributes');
                if (project && customAttributes) {
                    const input = customAttributes.map(({ id, value }) => ({
                        id,
                        value,
                    }));
                    await bulkUpdate({
                        variables: {
                            projectID: project.id,
                            input,
                        },
                    });
                }
                await updateProject({
                    variables: payload,
                });
                pushSuccessToast({
                    detail: `Project ${payload.name ?? project?.name ?? ''} updated.`,
                });
                if (closeDialog) {
                    closeDialog();
                }
                return true;
            } catch (err) {
                const detail =
                    err instanceof Error
                        ? err.message
                        : 'Error copying project';
                pushErrorToast({
                    detail,
                });
                return false;
            }
        },
        [
            bulkUpdate,
            getValues,
            getCheckedCountryIDs,
            project,
            pushErrorToast,
            pushSuccessToast,
            updateProject,
        ],
    );

    const handleProjectCreation = useCallback(async (): Promise<boolean> => {
        const countryIDs = getCheckedCountryIDs();
        const variables = { ...getValues(), countryIDs };
        try {
            const { data } = await createProject({ variables });
            const createdProjectId = data?.createProject?.id;
            if (!createdProjectId) {
                pushErrorToast({
                    detail: 'Error creating project',
                });
                return false;
            }
            pushSuccessToast({
                detail: `Project ${variables.name} created.`,
            });
            navigate(Route.Project(createdProjectId));
            return true;
        } catch (err) {
            const detail =
                err instanceof Error ? err.message : 'Error creating project';
            pushErrorToast({
                detail,
            });
            return false;
        }
    }, [
        createProject,
        getValues,
        getCheckedCountryIDs,
        navigate,
        pushErrorToast,
        pushSuccessToast,
    ]);

    const onSubmit = useCallback(
        async (closeDialog?: () => void): Promise<boolean> => {
            if (project) {
                return await handleProjectUpdate(closeDialog);
            }
            return await handleProjectCreation();
        },
        [project, handleProjectUpdate, handleProjectCreation],
    );

    return {
        form,
        onSubmit,
    };
}
